import React, { useReducer, useRef, useState, Dispatch } from 'react';
import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import Api from '../API';
import GenericTable from '../../Components/GenericTable/GenericTable';
import { CandidatePoolsReducer, InitialCandidatePoolsState } from '../reducer';
import { ICandidateSourcingPool, ICandidatePoolsApiResponse } from '../types';
import CandidatePoolTableRows from '../CandidatePoolTableRows';
import { headerStyle } from '../../Candidates/config';
import { candidatePoolsActionsList } from '../config';
import CandidatePoolsTableActionComponents from '../CandidatePoolsTableActionComponents';
import SearchBar from '../SearchBar';
import AddButton from '../../Components/GenericTable/AddButton';
import TotalSelectedIndicatorWithActions from '../../Components/GenericTable/TotalSelectedIndicatorWithActions';
import { PlaylistAdd } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import EmailCandidateModal from '../EmailCandidateModal';
import IndividualCandidatePoolModal from './IndividualCandidatePoolModal';
import {
  initialTableState,
  genericTableReducer
} from '../../Components/GenericTable/genericTableReducer';
import { candidatePoolAssignmentColumns } from './candidatePoolAssignmentConfig';
import { emailBatchColumns } from './emailBatchConfig';
import { CandidateSourcingAction } from '../candidateSourcingReducer';
import GenericDialog from '../../Components/Modals/GenericDialog';

interface CandidatePoolsProps {
  candidateSourcingDispatch: Dispatch<CandidateSourcingAction>;
}

const CandidatePools: React.FC<CandidatePoolsProps> = ({ candidateSourcingDispatch }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [candidatePoolsState, dispatch] = useReducer(
    CandidatePoolsReducer,
    InitialCandidatePoolsState
  );
  const [selectedPool, setSelectedPool] = useState<ICandidateSourcingPool | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTabValue, setModalTabValue] = useState(0);
  const [individualCandidatePoolsState, individualCandidatePoolsDispatch] = useReducer(
    genericTableReducer,
    initialTableState(candidatePoolAssignmentColumns, {
      sortBy: 'name',
      sortOrder: 'asc'
    })
  );
  const [emailsState, emailsDispatch] = useReducer(
    genericTableReducer,
    initialTableState(emailBatchColumns, {
      sortBy: 'sent_at',
      sortOrder: 'desc'
    })
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [poolToDelete, setPoolToDelete] = useState<ICandidateSourcingPool | null>(null);

  const {
    candidatePoolsTableState,
    candidatePoolsTableSettings,
    candidatePoolsCount,
    totalPages,
    searchQuery
  } = candidatePoolsState;

  const {
    data: candidatePoolsData,
    isLoading: isFetchingCandidatePools,
    refetch: refetchCandidatePools
  } = useQuery({
    queryKey: [
      'candidatePools',
      candidatePoolsTableSettings.page,
      candidatePoolsTableSettings.limit,
      candidatePoolsTableSettings.sort,
      candidatePoolsTableSettings.sort_order,
      searchQuery
    ],
    queryFn: async () => {
      const response = await Api.getCandidatePools(
        candidatePoolsTableSettings.page,
        candidatePoolsTableSettings.limit,
        candidatePoolsTableSettings.sort,
        candidatePoolsTableSettings.sort_order,
        searchQuery
      );
      const responseData = response as ICandidatePoolsApiResponse;

      if (responseData.resHead) {
        dispatch({
          type: 'SET_CANDIDATE_POOLS_COUNT',
          payload: parseInt(responseData.resHead['x-total-count'] || '0')
        });
        dispatch({
          type: 'SET_TOTAL_PAGES',
          payload: parseInt(responseData.resHead['x-total-pages'] || '0')
        });
      }

      return responseData;
    }
  });

  const handleCreateNewPool = () => {
    // TODO: Implement create new pool functionality
  };

  const handleSearchChange = (query: string) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: query });
  };

  const handleSearch = () => {
    refetchCandidatePools();
  };

  const handleClearSearch = () => {
    dispatch({ type: 'CLEAR_SEARCH_QUERY' });
  };

  const handleRowClick = (pool: ICandidateSourcingPool) => {
    setSelectedPool(pool);
    setIsModalOpen(true);
    setModalTabValue(0);
  };

  const handleAnalyticsClick = (pool: ICandidateSourcingPool) => {
    setSelectedPool(pool);
    setIsModalOpen(true);
    setModalTabValue(1);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPool(null);
    setModalTabValue(0);
  };

  const handleSnackbarState = () => {
    // Handle snackbar state changes if needed
  };

  const handleDeletePool = async () => {
    if (poolToDelete) {
      try {
        await Api.deleteCandidatePool(poolToDelete.id);
        refetchCandidatePools();
      } catch (error) {
        // Error handling for pool deletion
      }
    }
    setDeleteDialogOpen(false);
    setPoolToDelete(null);
  };

  const handleDeleteClick = (pool: ICandidateSourcingPool) => {
    setPoolToDelete(pool);
    setDeleteDialogOpen(true);
  };

  const candidatePools = candidatePoolsData?.res?.pools || [];

  return (
    <Stack sx={{ padding: '20px' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: '20px' }}
        ref={containerRef}
      >
        <Stack direction="row" spacing={2} sx={{ flex: 1, marginRight: '20px' }}>
          <SearchBar
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            placeholder="Search by name"
          />
        </Stack>
        <AddButton title="Create New Pool" onClick={handleCreateNewPool}>
          <PlaylistAdd />
        </AddButton>
      </Stack>

      <GenericTable
        label="candidate pools list"
        items={candidatePools as ICandidateSourcingPool[]}
        isLoadingItems={isFetchingCandidatePools}
        tableState={candidatePoolsTableState}
        dispatch={dispatch}
        headerStyle={headerStyle}
        paginationState={candidatePoolsTableSettings}
        pagination={{
          totalItems: candidatePoolsCount,
          totalPages
        }}
        TableRows={(props) => (
          <CandidatePoolTableRows {...props} dispatch={dispatch} onRowClick={handleRowClick} />
        )}
        TableActionList={candidatePoolsActionsList}
        allowMultipleSelection={candidatePoolsCount > 0}
        titleCellId="id"
        userPreferenceSource="candidatePools"
      />

      <TotalSelectedIndicatorWithActions
        totalSelected={candidatePoolsTableState.selectedItemIds.length}
        parentDiv={containerRef.current}
        dispatch={dispatch}
        actionButtonsList={[
          {
            id: 'email',
            label: 'Email',
            icon: <EmailIcon />
          }
        ]}
        actionButtonId="email-pool-button"
      />

      <EmailCandidateModal
        isOpen={candidatePoolsTableState.modalsOpen?.email || false}
        onClose={() =>
          dispatch({ type: 'SET_ACTION_MODAL_OPEN', payload: { name: 'email', isOpen: false } })
        }
        selectedPoolIds={candidatePoolsTableState.selectedItemIds}
      />

      {selectedPool && (
        <IndividualCandidatePoolModal
          isOpen={isModalOpen}
          selectedPool={selectedPool}
          individualCandidatePoolsState={individualCandidatePoolsState}
          individualCandidatePoolsDispatch={individualCandidatePoolsDispatch}
          emailsState={emailsState}
          emailsDispatch={emailsDispatch}
          handleClose={handleCloseModal}
          setSnackbarState={handleSnackbarState}
          refetch={refetchCandidatePools}
          modalTabValue={modalTabValue}
          setModalTabValue={setModalTabValue}
          candidateSourcingDispatch={candidateSourcingDispatch}
        />
      )}

      <CandidatePoolsTableActionComponents
        actionsState={candidatePoolsTableState.actions}
        dispatch={dispatch}
        refetchCandidatePools={refetchCandidatePools}
        onEmailAnalyticsClick={handleAnalyticsClick}
        onDeleteClick={handleDeleteClick}
      />

      <GenericDialog
        isDialogOpen={deleteDialogOpen}
        setDialogOpen={setDeleteDialogOpen}
        title="Delete this pool?"
        description="Are you sure you want to permanently delete this candidate pool? This action cannot be undone and all associated data will be deleted."
        buttonCallback={handleDeletePool}
        callbackLoading={false}
        buttonText="Delete"
        url=""
      />
    </Stack>
  );
};

export default CandidatePools;
